/**
 * BacktestTerminal.tsx
 *
 * Full-height terminal-style log display for backtest simulation.
 * Enhanced version of BacktestLog with darker styling and full height.
 */

import React, { useEffect, useRef } from 'react';

interface BacktestTerminalProps {
  logs: string[];
  isRunning: boolean;
}

const BacktestTerminal: React.FC<BacktestTerminalProps> = ({ logs, isRunning }) => {
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Tone log messages based on content
  const getLogClass = (log: string): string => {
    if (log.startsWith('---')) {
      // Strategy header
      return 'is-head';
    }
    if (log.startsWith('SCAN:')) {
      return 'is-dim'; // Dimmed for scanning
    }
    if (log.startsWith('SELECT:')) {
      return ''; // Contract selection
    }
    if (log.startsWith('SKIP:')) {
      return 'is-dim'; // Skipped days
    }
    if (log.startsWith('OPEN:')) {
      return 'is-open';
    }
    if (log.startsWith('CLOSE:') || log.startsWith('ASSIGNED:')) {
      // Check if positive or negative P&L
      if (log.includes('+$')) {
        return 'is-gain';
      }
      if (log.includes('-$')) {
        return 'is-loss';
      }
      return '';
    }
    if (log.startsWith('COMPLETE:')) {
      return 'is-done';
    }
    return ''; // Default
  };

  return (
    <div className="bt-term">
      {/* Header */}
      <div className="bt-panel-h">
        <span className="bt-term-live">
          <span className={`bt-live-dot${isRunning ? ' is-on' : ''}`} />
          <span className="bt-h">Live Simulation Log</span>
        </span>
        <span className="meta"><span className="n">{logs.length}</span> events</span>
      </div>

      {/* Log content */}
      <div ref={logContainerRef} className={`bt-term-body${''}`}>
        {logs.length === 0 ? (
          <div className="bt-term-empty">Waiting for simulation to start...</div>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              className={`bt-log ${getLogClass(log)}${index === logs.length - 1 ? ' is-new' : ''}`}
            >
              {log}
            </div>
          ))
        )}
        {isRunning && (
          <div className="bt-caret">
            <span>_</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BacktestTerminal;
